import { effect, inject } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  GuardResult,
} from '@angular/router';
import { Router } from '@angular/router';

import type { AuthOperation } from '#/app/shared/domain/role';
import { RoleService } from '#/app/shared/services/role.service';
import { TenantUserContext } from '#/app/shared/services/tenant-user.context';

/**
 * Guard to check if the user has all of the allowed operations to access a route.
 * To use:
 * ```typescript
 * {
 *   path: 'users',
 *   canActivate: [tenantRoleGuard(['OrganizationUserList'])],
 * }
 * ```
 */
export const tenantRoleGuard = (
  allowedOperations: AuthOperation[],
): CanActivateFn => {
  return (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const tenantUserContext = inject(TenantUserContext);
    const roleService = inject(RoleService);
    const tenantIdParam = route.paramMap.get('tenantId');

    if (allowedOperations.length === 0) {
      throw new Error(
        'roleGuard requires at least one operation to check against.',
      );
    }

    return new Promise<GuardResult>((resolve) => {
      const effectRef = effect(
        () => {
          if (tenantUserContext.isLoaded()) {
            effectRef.destroy();

            const isAuthorized =
              roleService.hasAllOperations(allowedOperations);
            if (!isAuthorized) {
              if (tenantIdParam) {
                resolve(
                  router.createUrlTree([`/o/${tenantIdParam}/no-access`], {
                    queryParams: { reason: 'role' },
                  }),
                );
              } else {
                // FIXME: This should be an error state - we should not be using this guard on routes without a tenantId param
                resolve(router.createUrlTree(['/no-access']));
              }
            } else {
              resolve(true);
            }
          }
        },
        { manualCleanup: true },
      );
    });
  };
};
